
import { useState, useEffect, useRef } from 'react';

// Module-level cache: tracks srcs that have already been loaded this session.
// Survives React component unmount/remount so back-navigation never shows a blank frame.
// Also populated by preloadImages() so preloaded images render instantly on first use.
export const shownImages = new Set();

const useLazyImage = (src) => {
  const [imageSrc, setImageSrc] = useState(() => (src && shownImages.has(src) ? src : null));
  const imageRef = useRef(null);

  useEffect(() => {
    if (!src) return;

    // Already confirmed-loaded before — set immediately without waiting for intersection.
    if (shownImages.has(src)) {
      setImageSrc(src);
      return;
    }

    let observer;
    let img;

    const loadImage = () => {
      img = new Image();
      img.onload = () => {
        shownImages.add(src);
        setImageSrc(src);
      };
      // On failure: don't cache — next mount will retry via the observer.
      img.onerror = () => {};
      img.src = src;
    };

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          loadImage();
        }
      });
    };

    if (imageRef.current) {
      observer = new IntersectionObserver(handleIntersection, {
        rootMargin: '0px',
        threshold: 0.1,
      });
      observer.observe(imageRef.current);
    }

    return () => {
      if (observer && imageRef.current) {
        observer.unobserve(imageRef.current);
      }
      // Prevent stale callbacks if src changes or component unmounts mid-load.
      if (img) {
        img.onload = null;
        img.onerror = null;
      }
    };
  }, [src]);

  return [imageSrc, imageRef];
};

export default useLazyImage;
